import React from 'react';
import { motion } from 'framer-motion';
import { Target, ChevronRight } from 'lucide-react';
import { AchievementDefinition } from './achievementDefinitions';

interface AchievementProgressCardProps {
  achievement: AchievementDefinition;
  progress: { current: number; target: number };
}

export const AchievementProgressCard: React.FC<AchievementProgressCardProps> = ({
  achievement,
  progress
}) => {
  const progressPercent = Math.min((progress.current / progress.target) * 100, 100);
  const remaining = Math.max(progress.target - progress.current, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-amber-500/10 to-orange-600/10 border border-amber-500/20 rounded-2xl p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-semibold text-amber-400">PRÓXIMO LOGRO</span>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 border border-amber-500/30 flex items-center justify-center">
          <span className="text-3xl grayscale-[50%]">{achievement.badge}</span>
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold">{achievement.name}</h4>
          <p className="text-white/60 text-sm">{achievement.description}</p>
          
          {/* Progress bar */}
          <div className="mt-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-white/40">
                {progress.current} / {progress.target}
              </span>
              <span className="text-xs text-amber-400">
                +{achievement.points} pts
              </span>
            </div>
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
              />
            </div>
          </div>
          
          {remaining > 0 && (
            <p className="text-xs text-amber-400/80 mt-2 flex items-center gap-1">
              <ChevronRight className="w-3 h-3" />
              ¡Te faltan {remaining} para desbloquear!
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AchievementProgressCard;
